import { fabric } from 'fabric';
import {
  currentCrop, DEFAULT_ROUND, displaySize, dragFrame, fitInside, fitRatio, flipView, minSide, quarterOf, RATIOS, ratioOf, readShape,
  stretchOf, toDisplay, toImage, turnView, type Handle, type PhotoShape, type Rect, type View
} from '../editor/crop';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { applyShape, naturalSize } from '../editor/picture';
import { $, $in, setPressed } from './dom';
import { toast } from './toast';

/*
 * "ครอป" (แต่งภาพ round 2, choice ก: a crop screen over the whole app) for the selected picture.
 * The frame is kept in the picture's own pixels (src/editor/crop.ts); "เสร็จ" writes Fabric's cropX/cropY/width/height,
 * the flips, the added quarter turns and the shape, and keeps the kept part where it was on the page.
 * Nothing changes on the page until "เสร็จ"; "ยกเลิก" leaves the picture as it was.
 */

type Pic = fabric.Image & { photoShape?: unknown };

const PREVIEW_MAX = 1600;   // the crop screen draws a copy at most this big (an iPad has no memory to spare)
const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);

export function initCropView(ed: Editor, history: History) {
  const { canvas } = ed;
  const root = $('crop'), stage = $('crStage'), cv = $<HTMLCanvasElement>('crCanvas'), frame = $('crFrame'), msg = $('crMsg');
  const say = (t: string, ok = false) => { msg.textContent = t; msg.hidden = !t; msg.classList.toggle('ok', ok); };

  let img: Pic | null = null;
  let small: HTMLCanvasElement | null = null;   // the picture (with its แต่งภาพ look) at preview size
  let W = 0, H = 0;                               // the picture file's pixels
  let rect: Rect = { x: 0, y: 0, w: 1, h: 1 };     // the kept part, picture px
  let view: View = { turn: 0, flipX: false, flipY: false };
  let baseTurn: View['turn'] = 0;                 // the quarter turn the picture already had on the page
  let ratioId = 'free';
  let shape: PhotoShape = readShape(null);
  let fit = { s: 1, ox: 0, oy: 0 };               // display px → stage CSS px (× ex / ey)
  let scale = { x: 1, y: 1 };                     // the picture's scale on the page (a stretched one shows stretched)

  const D = () => displaySize(W, H, view);
  const st = () => stretchOf(scale.x, scale.y, view);
  /** the chosen ratio as it looks on the page, in the screen picture's own px (null = free) */
  const ratio = () => {
    const d = D(), { ex, ey } = st();
    const r = ratioOf(ratioId, { w: d.w * ex, h: d.h * ey });
    return r ? r * ey / ex : null;
  };

  /* ---------- chips (made once) ---------- */
  for (const r of RATIOS) {
    const b = document.createElement('button');
    b.className = 'fchip'; b.dataset.v = r.id; b.textContent = r.name;
    b.onclick = () => { ratioId = r.id; applyRatio(); paintChips(); paintFrame(); };
    $('crRatios').append(b);
  }
  $('crShapes').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    const v = b.dataset.v as PhotoShape['shape'];
    shape = { shape: v, round: v === 'round' ? (shape.round || DEFAULT_ROUND) : 0 };
    // a circle starts square (a free frame would make an oval); another ratio can still be picked after
    if (v === 'circle' && (ratioId === 'free' || ratioId === 'orig')) {
      ratioId = '1:1';
      rect = toImage(fitInside(toDisplay(rect, W, H, view), ratio()!), W, H, view);   // inside the frame the user made
    }
    paintChips(); paintFrame();
  };
  const round = $in('crRound');
  round.oninput = () => { shape = { shape: 'round', round: Number(round.value) }; paintChips(); paintFrame(); };

  $('crFlipH').onclick = () => { view = flipView(view, 'h'); draw(); };
  $('crFlipV').onclick = () => { view = flipView(view, 'v'); draw(); };
  $('crTurn').onclick = () => {
    view = turnView(view);
    if (ratio()) applyRatio();   // a ratio is how it looks on screen: turned, the frame is fitted again
    draw();
  };
  $('crAll').onclick = () => {
    rect = { x: 0, y: 0, w: W, h: H };
    if (ratio()) applyRatio();
    paintFrame();
  };

  /** the largest frame of the chosen ratio around the frame's centre */
  function applyRatio() {
    const r = ratio(); if (!r) return;
    rect = toImage(fitRatio(toDisplay(rect, W, H, view), r, D()), W, H, view);
  }

  function paintChips() {
    setPressed('crRatios', b => b.dataset.v === ratioId);
    setPressed('crShapes', b => b.dataset.v === shape.shape);
    $('crRoundRow').hidden = shape.shape !== 'round';
    round.value = String(shape.round || DEFAULT_ROUND);
    $('crRoundV').textContent = `${shape.round}%`;
  }

  /* ---------- drawing ---------- */
  /** the picture, turned and flipped as it will look, fitted in the stage */
  function draw() {
    if (!small) return;
    const sw = stage.clientWidth, sh = stage.clientHeight, d = D(), dpr = window.devicePixelRatio || 1, { ex, ey } = st();
    const s = Math.max(0.0001, Math.min((sw - 48) / (d.w * ex), (sh - 48) / (d.h * ey)));
    fit = { s, ox: (sw - d.w * ex * s) / 2, oy: (sh - d.h * ey * s) / 2 };
    cv.width = Math.round(sw * dpr); cv.height = Math.round(sh * dpr);
    cv.style.width = `${sw}px`; cv.style.height = `${sh}px`;
    const g = cv.getContext('2d');
    if (!g) { say('เครื่องนี้วาดหน้าครอปไม่ได้ (หน่วยความจำไม่พอ) ลองปิดแอปอื่นแล้วลองใหม่'); return; }
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, sw, sh);
    // flip first, then turn (Fabric's order, the same as src/editor/crop.ts), then the page's stretch
    g.translate(fit.ox + d.w * ex * s / 2, fit.oy + d.h * ey * s / 2);
    g.scale(ex * s, ey * s);
    g.rotate(view.turn * Math.PI / 2);
    g.scale(view.flipX ? -1 : 1, view.flipY ? -1 : 1);
    g.drawImage(small, -W / 2, -H / 2, W, H);
    paintFrame();
  }

  function paintFrame() {
    const d = toDisplay(rect, W, H, view), { ex, ey } = st(), kx = fit.s * ex, ky = fit.s * ey;
    const r = Math.min(d.w, d.h) * shape.round / 100;   // the same corner as applyShape, stretched like the page
    Object.assign(frame.style, {
      left: `${fit.ox + d.x * kx}px`, top: `${fit.oy + d.y * ky}px`,
      width: `${d.w * kx}px`, height: `${d.h * ky}px`,
      borderRadius: shape.shape === 'round' ? `${r * kx}px / ${r * ky}px` : ''
    });
    frame.classList.toggle('circle', shape.shape === 'circle');
    frame.classList.toggle('locked', !!ratio());
    // a small frame: smaller handles, so the middle can still be dragged
    frame.classList.toggle('small', Math.min(d.w * kx, d.h * ky) < 90);
  }

  /* ---------- dragging the frame or a corner ---------- */
  let drag: { h: Handle; x: number; y: number; start: Rect; id: number } | null = null;
  frame.addEventListener('pointerdown', e => {
    const h = ((e.target as HTMLElement).dataset.h ?? 'move') as Handle;
    drag = { h, x: e.clientX, y: e.clientY, start: toDisplay(rect, W, H, view), id: e.pointerId };
    frame.setPointerCapture(e.pointerId);
    e.preventDefault();
  });
  frame.addEventListener('pointermove', e => {
    if (!drag || e.pointerId !== drag.id) return;
    const { ex, ey } = st();
    const nd = dragFrame(drag.start, drag.h, (e.clientX - drag.x) / (fit.s * ex), (e.clientY - drag.y) / (fit.s * ey), D(), ratio(), minSide(W, H));
    rect = toImage(nd, W, H, view);
    paintFrame();
  });
  const endDrag = (e: PointerEvent) => { if (drag && e.pointerId === drag.id) drag = null; };
  frame.addEventListener('pointerup', endDrag);
  frame.addEventListener('pointercancel', endDrag);

  /* ---------- open / close ---------- */
  function close() {
    root.hidden = true;
    img = null; drag = null;
    if (small) { small.width = small.height = 0; small = null; }   // free the copy now (iPhone)
    cv.width = cv.height = 0;
  }

  function open() {
    const o = canvas.getActiveObject() as Pic | null;
    if (!o || o.type !== 'image') return;
    const nat = naturalSize(o);
    const el = o.getElement() as HTMLImageElement | HTMLCanvasElement | null;   // with its แต่งภาพ look
    if (!el || !nat.w || !nat.h) { toast('รูปนี้ยังโหลดไม่เสร็จหรือเปิดไม่ได้ จึงครอปไม่ได้', true); return; }
    W = nat.w; H = nat.h;
    const k = Math.min(1, PREVIEW_MAX / Math.max(W, H));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(W * k)); c.height = Math.max(1, Math.round(H * k));
    const g = c.getContext('2d');
    if (!g) { toast('เครื่องนี้หน่วยความจำไม่พอสำหรับหน้าครอป ลองปิดแอปอื่นแล้วลองใหม่', true); return; }
    g.drawImage(el, 0, 0, c.width, c.height);
    small = c;
    img = o;
    rect = currentCrop(o, W, H);
    scale = { x: o.scaleX ?? 1, y: o.scaleY ?? 1 };
    baseTurn = quarterOf(o.angle);
    view = { turn: baseTurn, flipX: !!o.flipX, flipY: !!o.flipY };
    shape = readShape(o.photoShape);
    ratioId = 'free';
    say('');
    root.hidden = false;
    paintChips();
    draw();
  }

  /** write the crop to the picture: the kept part stays where it showed on the page */
  function done() {
    const o = img;
    if (!o) { close(); return; }
    if (!canvas.getObjects().includes(o)) { say('รูปนี้ไม่อยู่ในหน้านี้แล้ว (งานเปลี่ยนไประหว่างครอป) กด "ยกเลิก" แล้วเลือกรูปใหม่'); return; }
    // the frame was measured on the file the picture had when this screen opened (BUG-31)
    const nat = naturalSize(o);
    if (nat.w !== W || nat.h !== H) {
      say(`รูปนี้เพิ่งถูกเปลี่ยนไฟล์ระหว่างครอป (${W}×${H} → ${nat.w}×${nat.h} px เช่น แต่งภาพย่อรูปใหญ่) กรอบที่วางไว้จึงใช้ไม่ได้ กด "ยกเลิก" แล้วเปิดครอปใหม่`);
      return;
    }
    try {
      const old = currentCrop(o, W, H);
      // whole pixels, inside the file (the turn/flip maths can leave 1e-13 past the edge)
      const R = currentCrop({ cropX: Math.round(rect.x), cropY: Math.round(rect.y), width: Math.round(rect.w), height: Math.round(rect.h) }, W, H);
      // the new part's centre, where the picture shows it now (object space is centred on the old kept part)
      const at = fabric.util.transformPoint(
        new fabric.Point(R.x + R.w / 2 - (old.x + old.w / 2), R.y + R.h / 2 - (old.y + old.h / 2)),
        o.calcTransformMatrix());
      const turns = (view.turn - baseTurn + 4) % 4;
      o.set({
        cropX: R.x, cropY: R.y, width: R.w, height: R.h,
        flipX: view.flipX, flipY: view.flipY, angle: ((o.angle ?? 0) + turns * 90) % 360
      });
      o.set('photoShape' as keyof fabric.Object, shape.shape === 'rect' ? undefined : { ...shape });
      applyShape(o);
      o.setPositionByOrigin(at, 'center', 'center');
      o.setCoords();
      o.dirty = true;
      canvas.requestRenderAll();
      history.push();
      close();
    } catch (e) {
      say(`ครอปไม่สำเร็จ: ${errText(e)} (รูปยังเหมือนเดิม)`);
    }
  }

  $('crCancel').onclick = close;
  $('crDone').onclick = done;
  window.addEventListener('resize', () => { if (!root.hidden) draw(); });
  // while cropping, the editor's keys (delete, undo, arrows) must not act on the page behind; keys meant for
  // something above this screen (the sign-in gate) are left alone
  window.addEventListener('keydown', e => {
    if (root.hidden) return;
    const t = e.target as Node | null;
    if (t && t !== document.body && t !== document.documentElement && !root.contains(t)) return;
    e.stopPropagation();
    if (e.key === 'Escape') close();
    else if (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement)) done();
  }, true);

  // messages of the app (saving failed, …) show under this screen: repeat them here so none goes unseen
  const toastEl = document.getElementById('toast');
  if (toastEl) new MutationObserver(() => {
    if (!root.hidden && !toastEl.hidden && toastEl.textContent) say(toastEl.textContent, !toastEl.classList.contains('err'));
  }).observe(toastEl, { attributes: true, attributeFilter: ['hidden'], childList: true, characterData: true, subtree: true });

  return { open, isOpen: () => !root.hidden };
}
